import { extractErrorMessage } from './extractErrorMessage.ts';

const retryHint = 'Try again in a moment.';

/**
 * What to do next after a failed request. Only a 4xx carries API detail
 * written for the user (validation, plan limits, conflicts). A 5xx, the
 * `HTTP 500: …` line `errorMiddleware` falls back to, or a network failure
 * would leak internals, so those get a generic next step instead.
 */
export const errorHint = (error: unknown): string => {
  // fetch rejects with a TypeError ("Failed to fetch") when the request never
  // reached the API.
  if (error instanceof TypeError) return 'Check your connection and try again.';

  const status =
    error && typeof error === 'object' && 'status' in error
      ? error.status
      : undefined;
  if (typeof status !== 'number' || status < 400 || status >= 500) {
    return retryHint;
  }
  if (status === 401) return 'Sign in again, then try again.';
  if (status === 403) return 'Your account does not have access to do this.';
  if (status === 404) return 'It may have been removed. Refresh the page.';

  const detail = extractErrorMessage(error).trim();
  if (!detail || detail.startsWith('HTTP ')) return retryHint;
  return /[.!?]$/.test(detail) ? detail : `${detail}.`;
};

/**
 * The one sentence a user sees when an action fails, e.g.
 * `Couldn't close the job. Try again in a moment.` `action` is the verb
 * phrase the user tried: "close the job", "send the messages".
 */
export const actionErrorMessage = (action: string, error: unknown): string =>
  `Couldn't ${action}. ${errorHint(error)}`;
